<script lang="ts">
	import { onMount } from 'svelte';
	import { goto } from '$app/navigation';
	import { userStore } from '$lib/stores/user';
	import { api } from '$lib/services/api';
	import { t } from 'svelte-i18n';

	let notifications = $state<Array<{ id: string; type: string; createdAt: string; readAt: string | null }>>([]);
	let loaded = $state(false);
	let dismissed = $state(false);

	async function fetchNotifications() {
		try {
			const list = await api.notifications.list();
			notifications = list ?? [];
		} catch {
			notifications = [];
		} finally {
			loaded = true;
		}
	}

	// Study reminders only make sense once a user has something to study —
	// never show this banner to a "user"-role account that hasn't completed
	// onboarding yet. Other roles (admin/teacher/student) skip onboarding
	// entirely, matching the redirect check in +layout.svelte.
	async function maybeFetchNotifications(role: string | undefined) {
		if (role === "user") {
			try {
				const { onboarded } = await api.onboarding.status();
				if (!onboarded) {
					notifications = [];
					loaded = true;
					return;
				}
			} catch {
				// Can't confirm onboarding status — fail closed rather than
				// risk reminding someone who has nothing to study yet.
				notifications = [];
				loaded = true;
				return;
			}
		}
		fetchNotifications();
	}

	onMount(() => {
		const unsubscribe = userStore.subscribe((state) => {
			if (!state.initialised) return;
			if (state.isAuthenticated) {
				maybeFetchNotifications(state.user?.role);
			} else {
				notifications = [];
				loaded = true;
			}
		});
		return unsubscribe;
	});

	const unread = $derived(notifications.filter((n) => n.readAt == null));
	const show = $derived(loaded && unread.length > 0 && !dismissed);

	async function dismiss() {
		try {
			await api.notifications.markAllRead();
			dismissed = true;
			notifications = notifications.map((n) => ({ ...n, readAt: n.createdAt }));
		} catch {
			dismissed = true;
		}
	}

	function goToStudy() {
		dismiss();
		goto('/dashboard');
	}
</script>

{#if show}
	<div
		class="flex items-center justify-between gap-4 px-4 py-3 bg-(--primary) text-(--primary-foreground) text-sm"
	>
		<span>{$t('reminder.study_time')}</span>
		<div class="flex items-center gap-2 shrink-0">
			<button
				type="button"
				onclick={goToStudy}
				class="rounded-xs bg-white/20 px-3 py-1.5 font-medium hover:bg-white/30"
			>
				{$t('reminder.open_dashboard')}
			</button>
			<button
				type="button"
				onclick={dismiss}
				class="rounded-xs px-2 py-1.5 opacity-90 hover:opacity-100"
				aria-label={$t('shared.dismiss')}
			>
				×
			</button>
		</div>
	</div>
{/if}
